import { capitalize, isNil, isString } from 'lodash';
import {
  FilterInfoOperator,
  FormattingFilter,
  FormattingOperator,
  FormattingRuleOperator,
  EMPTY,
  ColorRangeFormatting,
  isColorRangeFormatting,
  isListFieldFormatting,
  ListFieldFormatting,
  isRelativeDateValue,
} from '@ardoq/api-types';
import type { RelativeDateValue } from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { isISO8601DateOnly, relativeDateDisplayLabel } from '@ardoq/date-time';
import { type I18nLocale, t } from '@ardoq/i18n';
import { buildColorScale, formatRelativeDateValue } from '@ardoq/perspectives';
import {
  filterTypeByApiFieldType,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import type { CompleteColorRange } from '@ardoq/perspectives';
import { ConditionalFormattingInfo } from './types';
import { ordinalSuffixOf } from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { systemAttributeLabelMap } from '@ardoq/filter-interface';
import {
  FilterDescriptionJSX,
  LIST_FIELD_PSEUDO_COMPARATOR,
} from './conditionalFormattingDescriptions';
import { usersOperations, fieldOperations } from '@ardoq/core';
import {
  DEFAULT_OPERATOR_LABEL_KEY,
  OperatorLabel,
  COMPONENT_BUILT_IN_FIELDS,
  resolveLabel,
} from '@ardoq/perspectives';
import { couldBeArdoqId } from './buildMinimalScopeDataForFilters';

type ComparatorMapValue = [label: OperatorLabel, inverseLabel?: OperatorLabel];

const comparatorMapCache = new WeakMap<
  I18nLocale,
  Record<string | FormattingOperator, ComparatorMapValue>
>();

const getComparatorMap = (
  i18nLocale: I18nLocale
): Record<string | FormattingOperator, ComparatorMapValue> => {
  const cached = comparatorMapCache.get(i18nLocale);
  if (cached) return cached;
  const comparatorMap = buildComparatorMap(i18nLocale);
  comparatorMapCache.set(i18nLocale, comparatorMap);
  return comparatorMap;
};

const buildComparatorMap = (
  i18nLocale: I18nLocale
): Record<string | FormattingOperator, ComparatorMapValue> => {
  const is = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.is',
    'is'
  );
  const isNot = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.isNot',
    'is not'
  );
  const contains = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.contains',
    'contains'
  );
  const doesNotContain = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.doesNotContain',
    'does not contain'
  );
  const isAfter = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.isAfter',
    'is after'
  );
  const isBefore = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.isBefore',
    'is before'
  );
  const isNotSet = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.isNotSet',
    'is not set'
  );
  return {
    boolean: [is],
    equals: [is, isNot],
    contains: [contains, doesNotContain],
    greaterThan: [
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isGreaterThan',
        'is greater than'
      ),
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isNotGreaterThan',
        'is not greater than'
      ),
    ],
    lessThan: [
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isLessThan',
        'is less than'
      ),
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isNotLessThan',
        'is not less than'
      ),
    ],
    idMatch: [is, isNot],
    dateLater: [
      isAfter,
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isNotAfter',
        'is not after'
      ),
    ],
    dateEarlier: [
      isBefore,
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isNotBefore',
        'is not before'
      ),
    ],
    dateMatch: [is, isNot],
    [FilterInfoOperator.DATE_RANGE_CONTAINS]: [contains],
    [FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN]: [doesNotContain],
    [FilterInfoOperator.DATE_RANGE_BEFORE]: [isBefore],
    [FilterInfoOperator.DATE_RANGE_AFTER]: [isAfter],
    [FormattingRuleOperator.COLOR_RANGE]: ['🌈'],
    [FormattingRuleOperator.ID_DOES_NOT_MATCH]: [isNot],
    [FormattingRuleOperator.NOT_EQUAL]: [isNot],
    [FormattingRuleOperator.NOT_BOOLEAN]: [is],
    [FormattingRuleOperator.NOT_CONTAINS]: [doesNotContain],
    [FormattingRuleOperator.GREATER_THAN_OR_EQUAL_TO]: [
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isGreaterThanOrEqualTo',
        'is greater than or equal to'
      ),
    ],
    [FormattingRuleOperator.LESS_THAN_OR_EQUAL_TO]: [
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isLessThanOrEqualTo',
        'is less than or equal to'
      ),
    ],
    [FormattingRuleOperator.BETWEEN]: [
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isBetween',
        'is between'
      ),
    ],
    [FormattingRuleOperator.NOT_BETWEEN]: [
      t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.isNotBetween',
        'is not between'
      ),
    ],
    [FormattingRuleOperator.RELATIVE_DATE]: [
      t(i18nLocale, 'viewLegend:conditionalFormatting.operator.isIn', 'is in'),
    ],
    [FormattingRuleOperator.IS_EMPTY]: [
      {
        [QueryBuilderFilterType.BOOLEAN]: isNotSet,
        [QueryBuilderFilterType.DATE]: isNotSet,
        [QueryBuilderFilterType.DATE_RANGE]: isNotSet,
        [QueryBuilderFilterType.DATE_TIME]: isNotSet,
        [DEFAULT_OPERATOR_LABEL_KEY]: t(
          i18nLocale,
          'viewLegend:conditionalFormatting.operator.hasNoValue',
          'has no value'
        ),
      },
    ],
  };
};

const getFieldFilterType = (
  scopeData: EnhancedScopeData,
  name: string
): QueryBuilderFilterType | undefined => {
  // we need this check because we don`t have QueryBuilderFilterType.DATE_RANGE in filterTypeByApiFieldType
  if (dateRangeOperations.fieldNameIsPartOfDateRangeField(name)) {
    return QueryBuilderFilterType.DATE_RANGE;
  }
  const field = enhancedScopeDataOperations.getFieldByName(scopeData, name);
  return field
    ? filterTypeByApiFieldType(true)[field.type]
    : // this fallback is necessary for handling 'Created' and 'Last updated' because these fields don`t exist in scopeData
      COMPONENT_BUILT_IN_FIELDS[name];
};

const filterAttributesLabelsOverrideCache = new WeakMap<
  I18nLocale,
  Record<string, string>
>();

const getFilterAttributesLabelsOverride = (
  i18nLocale: I18nLocale
): Record<string, string> => {
  const cached = filterAttributesLabelsOverrideCache.get(i18nLocale);
  if (cached) return cached;
  const labelsOverride = buildFilterAttributesLabelsOverride(i18nLocale);
  filterAttributesLabelsOverrideCache.set(i18nLocale, labelsOverride);
  return labelsOverride;
};

const buildFilterAttributesLabelsOverride = (
  i18nLocale: I18nLocale
): Record<string, string> => ({
  _version: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.version',
    'Version'
  ),
  incomingRefCount: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.incomingReferenceCount',
    'Incoming reference count'
  ),
  outgoingRefCount: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.outgoingReferenceCount',
    'Outgoing reference count'
  ),
  targetWorkspace: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.targetWorkspace',
    'Target workspace'
  ),
  rootWorkspace: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.sourceWorkspace',
    'Source workspace'
  ),
  parent: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.parent',
    'Parent'
  ),
  source: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.source',
    'Source'
  ),
  target: t(
    i18nLocale,
    'viewLegend:conditionalFormatting.attribute.target',
    'Target'
  ),
});

const getCheckedDisplayValue = (i18nLocale: I18nLocale): string =>
  t(i18nLocale, 'viewLegend:conditionalFormatting.checked', 'checked');
const getUncheckedDisplayValue = (i18nLocale: I18nLocale): string =>
  t(i18nLocale, 'viewLegend:conditionalFormatting.unchecked', 'unchecked');

const resolveEntityName = (
  scopeData: EnhancedScopeData,
  filter: FormattingFilter,
  value: string
): string | null => {
  // Check for workspace ID
  const workspace = enhancedScopeDataOperations.getWorkspace(scopeData, value);
  if (workspace) {
    return `"${workspace.name}"`;
  }

  // Check for component ID
  const component = enhancedScopeDataOperations.getComponent(scopeData, value);
  if (component) {
    return `"${component.name}"`;
  }

  // Check for field ID
  const field = enhancedScopeDataOperations.getField(scopeData, value);
  if (field) {
    return `"${field.name}"`;
  }

  // Check for user ID
  const user = enhancedScopeDataOperations.getOrganizationUser(
    scopeData,
    value
  );
  if (user) {
    return usersOperations.getUserDisplayText(user);
  }

  // Check for date pattern
  if (isISO8601DateOnly(value)) {
    return value;
  }

  // Check if it is a number
  if (!isNaN(Number(value))) {
    return value;
  }

  // If the value looks like an ArdoqID but wasn't resolved above
  // — show a placeholder instead of the raw ID.
  if (filter.affectComponent && couldBeArdoqId(value)) {
    return '"[Not In Dataset]"';
  }

  return null;
};

const getFieldLabelFromScopeData = (
  scopeData: EnhancedScopeData,
  name: string
): string | null => {
  // Check if it's a date range field
  const { fields } = dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(
    scopeData.fields
  );
  const field = fields.find(field => field.name === name);
  if (field) {
    return field.label;
  }

  // Check regular fields by name
  const attributeField = enhancedScopeDataOperations.getFieldByName(
    scopeData,
    name
  );
  if (attributeField) {
    return attributeField.label;
  }

  return null;
};

const getFieldLabel = (
  scopeData: EnhancedScopeData,
  name: string,
  affectComponent: boolean,
  affectReference: boolean,
  i18nLocale: I18nLocale
): string | null => {
  if (affectComponent || affectReference) {
    // First check system attributes
    const overridenAttributeLabel =
      getFilterAttributesLabelsOverride(i18nLocale)[name];
    if (overridenAttributeLabel) {
      return overridenAttributeLabel;
    }

    const defaultSystemLabel = systemAttributeLabelMap.get(name);
    if (defaultSystemLabel) {
      return defaultSystemLabel;
    }

    // Then check custom fields
    return getFieldLabelFromScopeData(scopeData, name);
  }
  return null;
};

const parseConditionalFormattingTags = (
  filter: FormattingFilter,
  i18nLocale: I18nLocale,
  isNewConditionalFormatting?: boolean
) => {
  const { id, color, name, isNegative } = filter;

  const tagLabel = t(i18nLocale, 'viewLegend:conditionalFormatting.tag', 'Tag');
  const comparator = isNegative
    ? t(i18nLocale, 'viewLegend:conditionalFormatting.operator.isNot', 'is not')
    : t(i18nLocale, 'viewLegend:conditionalFormatting.operator.is', 'is');

  const filterDescription = isNewConditionalFormatting
    ? `${tagLabel} ${comparator} "${name}"`
    : isNegative
      ? `${t(i18nLocale, 'viewLegend:conditionalFormatting.not', 'Not')} "${name}"`
      : name;

  return [
    {
      id,
      filterType: 'tags',
      color,
      filterDescription,
      ...(isNewConditionalFormatting
        ? {
            filterDescriptionJSX: FilterDescriptionJSX(i18nLocale, {
              fieldNameWithPart: tagLabel,
              comparator: null,
              comparatorParsed: comparator,
              valueParsed: `"${name}"`,
            }),
          }
        : {}),
    },
  ];
};

const DATE_RANGE_COMPARATORS = new Set<string>([
  FilterInfoOperator.DATE_RANGE_BEFORE,
  FilterInfoOperator.DATE_RANGE_AFTER,
  FilterInfoOperator.DATE_RANGE_CONTAINS,
  FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN,
]);
const DATE_COMPARATORS = new Set<string>([
  FilterInfoOperator.DATE_EARLIER,
  FilterInfoOperator.DATE_LATER,
  FilterInfoOperator.DATE_MATCH,
  ...DATE_RANGE_COMPARATORS,
]);

const parseName = (
  scopeData: EnhancedScopeData,
  name: string,
  affectComponent: boolean,
  affectReference: boolean,
  i18nLocale: I18nLocale
): string =>
  getFieldLabel(
    scopeData,
    name,
    affectComponent,
    affectReference,
    i18nLocale
  ) || capitalize(name);

type ParseFormattingArgsBase = {
  scopeData: EnhancedScopeData;
  name: string;
  affectComponent: boolean;
  affectReference: boolean;
  isNewConditionalFormatting: boolean;
  id?: string;
  i18nLocale: I18nLocale;
};
type ParseColorRangeFormattingArgs = ParseFormattingArgsBase & {
  value: ColorRangeFormatting;
};

const parseColorRangeFormatting = ({
  value,
  scopeData,
  name,
  affectComponent,
  affectReference,
  isNewConditionalFormatting,
  id,
  i18nLocale,
}: ParseColorRangeFormattingArgs) => {
  const nameParsed = parseName(
    scopeData,
    name,
    affectComponent,
    affectReference,
    i18nLocale
  );
  const filterType = affectComponent ? 'components' : 'references';

  const minVal = value.min.value;
  const maxVal = value.max.value;

  // If either min or max is not defined, we cannot create a legend items
  if (isNil(minVal) || isNil(maxVal)) {
    return [];
  }

  const formatValue = (val: number) => {
    // if value is integer, show as integer, else show one decimal place
    return val % 1 === 0 ? val.toString() : val.toFixed(1);
  };

  const formatRangeText = (from: number, to: number, mode: string) => {
    const fromFormatted = formatValue(from);
    const toFormatted = formatValue(to);

    if (mode === 'percent') {
      return `${fromFormatted} - ${toFormatted}%`;
    } else if (mode === 'percentile') {
      const toNum = Math.floor(to); // Use floor to get the integer part for ordinal suffix
      return `${fromFormatted} - ${ordinalSuffixOf(toNum)} ${t(
        i18nLocale,
        'viewLegend:conditionalFormatting.percentile',
        'percentile'
      )}`;
    }
    // mode === 'number'
    return `${fromFormatted} - ${toFormatted}`;
  };

  const completeColorRange: CompleteColorRange = {
    ...value,
    min: { ...value.min, value: minVal },
    max: { ...value.max, value: maxVal },
  };

  const colors = buildColorScale(completeColorRange);

  // Create text buckets: 5 equal textual ranges using 6 rounded boundaries
  // Make non-first ranges exclusive on the lower bound (+1) to avoid overlap
  const textTotalRange = maxVal - minVal;
  const textBoundaryPoints = [
    minVal,
    Math.round(minVal + textTotalRange / 5),
    Math.round(minVal + (textTotalRange * 2) / 5),
    Math.round(minVal + (textTotalRange * 3) / 5),
    Math.round(minVal + (textTotalRange * 4) / 5),
    maxVal,
  ];

  // Map each textual range to a color sampled at the corresponding visualization point index
  return textBoundaryPoints.slice(0, -1).map((textFromBoundary, i) => {
    const textToBoundary = textBoundaryPoints[i + 1];
    const textFromDisplayed = i === 0 ? textFromBoundary : textFromBoundary + 1;
    const rangeText = formatRangeText(
      textFromDisplayed,
      textToBoundary,
      value.mode
    );

    return {
      id,
      filterType,
      color: colors[i],
      filterDescription: `${nameParsed} ${rangeText}`,
      ...(isNewConditionalFormatting
        ? {
            filterDescriptionJSX: FilterDescriptionJSX(i18nLocale, {
              fieldNameWithPart: nameParsed,
              comparator: FormattingRuleOperator.COLOR_RANGE,
              comparatorParsed: '',
              valueParsed: rangeText,
            }),
          }
        : {}),
    };
  });
};

type ParseRelativeDateFormattingArgs = ParseFormattingArgsBase & {
  value: RelativeDateValue;
  comparator: FormattingOperator | null;
  isNegative: boolean;
  color?: string;
  i18nLocale: I18nLocale;
};

const parseRelativeDateFormatting = ({
  value,
  scopeData,
  name,
  affectComponent,
  affectReference,
  isNewConditionalFormatting,
  id,
  comparator,
  isNegative,
  color,
  i18nLocale,
}: ParseRelativeDateFormattingArgs) => {
  const fieldNameWithPart = getFieldNameRepresentation({
    scopeData,
    name,
    comparator,
    affectComponent,
    affectReference,
    i18nLocale,
  });

  const filterType = affectComponent ? 'components' : 'references';

  const comparatorParsed =
    comparator !== null
      ? resolveLabel(
          i18nLocale,
          getComparatorMap(i18nLocale)[comparator]?.[isNegative ? 1 : 0] ?? '',
          undefined
        )
      : '';

  const valueParsed = formatRelativeDateValue(value);

  const filterDescription = `${fieldNameWithPart} ${comparatorParsed} ${valueParsed}`;

  return [
    {
      id,
      filterType,
      color,
      filterDescription,
      ...(isNewConditionalFormatting
        ? {
            filterDescriptionJSX: FilterDescriptionJSX(i18nLocale, {
              fieldNameWithPart,
              comparator,
              comparatorParsed: comparatorParsed || '',
              valueParsed,
            }),
          }
        : {}),
    },
  ];
};

type ParseListfieldFormattingArgs = ParseFormattingArgsBase & {
  value: ListFieldFormatting;
};
const parseListFieldFormatting = ({
  value,
  scopeData,
  name,
  affectComponent,
  affectReference,
  isNewConditionalFormatting,
  id,
  i18nLocale,
}: ParseListfieldFormattingArgs) => {
  const nameParsed = parseName(
    scopeData,
    name,
    affectComponent,
    affectReference,
    i18nLocale
  );

  const filterType = affectComponent ? 'components' : 'references';

  const field = enhancedScopeDataOperations.getFieldByName(scopeData, name);
  if (!field) {
    return [];
  }

  const currentFieldOptions = fieldOperations.getListFieldOptions(field);
  const currentOptionsSet = new Set(currentFieldOptions);

  return Object.entries(value)
    .filter(([optionValue, config]) => {
      if (config?.isDisabled) {
        return false;
      }
      return optionValue === EMPTY || currentOptionsSet.has(optionValue);
    })
    .map(([optionValue, config]) => ({
      id,
      filterType,
      color: config?.color,
      filterDescription:
        optionValue === EMPTY
          ? `${nameParsed} ${t(
              i18nLocale,
              'viewLegend:conditionalFormatting.hasNoValue',
              'has no value'
            )}`
          : `${nameParsed} ${t(
              i18nLocale,
              'viewLegend:conditionalFormatting.operator.is',
              'is'
            )} "${optionValue}"`,
      ...(isNewConditionalFormatting
        ? {
            filterDescriptionJSX: FilterDescriptionJSX(i18nLocale, {
              fieldNameWithPart: nameParsed,
              comparator: LIST_FIELD_PSEUDO_COMPARATOR,
              comparatorParsed: '',
              valueParsed: optionValue,
            }),
          }
        : {}),
    }));
};

type GetFieldNameRepresentationArgs = {
  name: string;
  comparator: FormattingOperator | null;
  scopeData: EnhancedScopeData;
  affectComponent: boolean;
  affectReference: boolean;
  i18nLocale: I18nLocale;
};
const getFieldNameRepresentation = ({
  name,
  comparator,
  scopeData,
  affectComponent,
  affectReference,
  i18nLocale,
}: GetFieldNameRepresentationArgs) => {
  const isDateRangeField =
    name && dateRangeOperations.fieldNameIsPartOfDateRangeField(name);
  const isDateRangeStart =
    isDateRangeField &&
    dateRangeOperations.fieldNameIsDateRangeStartField(name);
  const isDateRangeComparator =
    comparator && DATE_RANGE_COMPARATORS.has(comparator);

  // Date range part comparators are the same as date without the range
  // so if field is a part of date range field and comparator is not a date range
  // comparator we need add "start" or "end" to the field name
  const dateRangeSuffix =
    isDateRangeField && !isDateRangeComparator
      ? isDateRangeStart
        ? ` ${t(i18nLocale, 'viewLegend:conditionalFormatting.dateRangeStart', 'start')}`
        : ` ${t(i18nLocale, 'viewLegend:conditionalFormatting.dateRangeEnd', 'end')}`
      : '';

  const nameToParse = isDateRangeField
    ? dateRangeOperations.extractDateRangeFieldName(name)
    : name;

  const parsedName = parseName(
    scopeData,
    nameToParse,
    affectComponent,
    affectReference,
    i18nLocale
  );

  return `${parsedName}${dateRangeSuffix}`;
};

const resolveComparatorDisplayValue = (
  i18nLocale: I18nLocale,
  comparator: FormattingOperator | null,
  isNegative: boolean,
  value: unknown,
  isNewConditionalFormatting: boolean,
  filterType: QueryBuilderFilterType | undefined
): string => {
  // For new conditional formatting: numeric comparisons use explicit comparators
  // (EQUALS vs NOT_EQUAL) instead of relying on isNegative flag
  if (isNewConditionalFormatting && typeof value === 'number') {
    if (comparator === FilterInfoOperator.EQUALS) {
      return t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.equalTo',
        'equal to'
      );
    }
    if (comparator === FormattingRuleOperator.NOT_EQUAL) {
      return t(
        i18nLocale,
        'viewLegend:conditionalFormatting.operator.notEqualTo',
        'not equal to'
      );
    }
  }

  if (comparator === null) return '';

  const label = getComparatorMap(i18nLocale)[comparator]?.[isNegative ? 1 : 0];
  if (!label) return '';
  return resolveLabel(i18nLocale, label, filterType);
};

const parseConditionalFormattingFilterValue = (
  scopeData: EnhancedScopeData,
  comparator: FormattingOperator,
  filter: FormattingFilter,
  value: unknown,
  i18nLocale: I18nLocale
) => {
  const isDateComparator = DATE_COMPARATORS.has(comparator);
  const isBooleanComparator = comparator === FilterInfoOperator.BOOLEAN;
  const isNotBooleanComparator =
    comparator === FormattingRuleOperator.NOT_BOOLEAN;

  if (comparator === FormattingRuleOperator.IS_EMPTY) return '';

  return (
    (isDateComparator &&
      isString(value) &&
      relativeDateDisplayLabel(value, i18nLocale)) ||
    (isBooleanComparator &&
      (value
        ? getCheckedDisplayValue(i18nLocale)
        : getUncheckedDisplayValue(i18nLocale))) ||
    (isNotBooleanComparator &&
      (value
        ? getUncheckedDisplayValue(i18nLocale)
        : getCheckedDisplayValue(i18nLocale))) ||
    (typeof value === 'string' &&
      resolveEntityName(scopeData, filter, value)) ||
    `"${value}"`
  );
};

const parseConditionalFormattingComponentsReferences = (
  scopeData: EnhancedScopeData,
  filter: FormattingFilter,
  i18nLocale: I18nLocale,
  isNewConditionalFormatting = false
) => {
  const {
    id,
    color,
    affectComponent,
    affectReference,
    name,
    isNegative,
    comparator = null,
    value,
  } = filter;

  // Handle List Field Formatting: expand into multiple legend items
  if (isListFieldFormatting(value)) {
    return parseListFieldFormatting({
      value,
      scopeData,
      name,
      affectComponent,
      affectReference,
      isNewConditionalFormatting,
      id,
      i18nLocale,
    });
  }

  // Handle Color Range Formatting: expand into multiple legend items
  if (isColorRangeFormatting(value)) {
    return parseColorRangeFormatting({
      value,
      scopeData,
      name,
      affectComponent,
      affectReference,
      isNewConditionalFormatting,
      id,
      i18nLocale,
    });
  }

  // Handle Relative Date Formatting
  if (isRelativeDateValue(value)) {
    return parseRelativeDateFormatting({
      value,
      scopeData,
      name,
      affectComponent,
      affectReference,
      isNewConditionalFormatting,
      id,
      comparator,
      isNegative,
      color,
      i18nLocale,
    });
  }

  const fieldNameWithPart = getFieldNameRepresentation({
    scopeData,
    name,
    comparator,
    affectComponent,
    affectReference,
    i18nLocale,
  });

  const comparatorParsed = resolveComparatorDisplayValue(
    i18nLocale,
    comparator,
    isNegative,
    value,
    isNewConditionalFormatting,
    getFieldFilterType(scopeData, name)
  );

  const valueParsed = comparator
    ? parseConditionalFormattingFilterValue(
        scopeData,
        comparator,
        filter,
        value,
        i18nLocale
      )
    : `"${value}"`;

  const filterDescription =
    `${fieldNameWithPart} ${comparatorParsed} ${valueParsed}`.trim();

  return [
    {
      id,
      filterType: affectComponent ? 'components' : 'references',
      color,
      filterDescription,
      ...(isNewConditionalFormatting
        ? {
            filterDescriptionJSX: FilterDescriptionJSX(i18nLocale, {
              fieldNameWithPart,
              comparator,
              comparatorParsed: comparatorParsed || '',
              valueParsed,
            }),
          }
        : {}),
    },
  ];
};

export const parseConditionalFormatting = (
  scopeData: EnhancedScopeData,
  filter: FormattingFilter,
  i18nLocale: I18nLocale,
  isNewConditionalFormatting?: boolean
): ConditionalFormattingInfo[] => {
  const { affectComponent, affectReference } = filter;
  if (affectComponent && affectReference) {
    return parseConditionalFormattingTags(
      filter,
      i18nLocale,
      isNewConditionalFormatting
    );
  } else if (affectComponent || affectReference) {
    return parseConditionalFormattingComponentsReferences(
      scopeData,
      filter,
      i18nLocale,
      isNewConditionalFormatting
    );
  }
  return [
    {
      id: undefined,
      filterType: undefined,
      color: undefined,
      filterDescription: undefined,
    },
  ];
};
